import { useState, useEffect, useCallback } from 'react';
import {
  MapPin,
  Star,
  Eye,
  Phone,
  Navigation,
  Globe,
  RefreshCw,
  Plus,
  Search,
  Loader2,
  MessageSquare,
  Send,
  X,
  FileText,
  Camera,
  Info
} from 'lucide-react';
import api, { GBPDashboard, GBPLocation, GBPReview, GBPPost, GBPAccount, GBPDailyInsight } from '../services/api';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useClient } from '../contexts/ClientContext';

type TabType = 'overview' | 'locations' | 'reviews' | 'posts';

const GBP = () => {
  const { selectedClientId } = useClient();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<GBPDashboard | null>(null);
  const [locations, setLocations] = useState<GBPLocation[]>([]);
  const [reviews, setReviews] = useState<GBPReview[]>([]);
  const [posts, setPosts] = useState<GBPPost[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('all');
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [replyFilter, setReplyFilter] = useState('all');
  const [syncing, setSyncing] = useState<string | null>(null);

  // Reply modal state
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState<GBPReview | null>(null);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  // Chart state
  const [dailyInsights, setDailyInsights] = useState<GBPDailyInsight[]>([]);
  const [chartPeriod, setChartPeriod] = useState<number>(30);
  const [chartGranularity, setChartGranularity] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [chartLoading, setChartLoading] = useState(false);
  // Location filter is shared via selectedLocation state

  // Create Location modal state
  const [showCreateLocationModal, setShowCreateLocationModal] = useState(false);
  const [createLocationAccountId, setCreateLocationAccountId] = useState<string>('');
  const [createLocationSubmitting, setCreateLocationSubmitting] = useState(false);
  const [createLocationError, setCreateLocationError] = useState<string | null>(null);
  const [newLocationData, setNewLocationData] = useState({
    title: '',
    phone: '',
    website: '',
    primary_category_name: '',
    service_areas: '',
    description: '',
  });
  const [newLocationHours, setNewLocationHours] = useState<Record<string, { enabled: boolean; open_time: string; close_time: string }>>({
    MONDAY: { enabled: false, open_time: '09:00', close_time: '17:00' },
    TUESDAY: { enabled: false, open_time: '09:00', close_time: '17:00' },
    WEDNESDAY: { enabled: false, open_time: '09:00', close_time: '17:00' },
    THURSDAY: { enabled: false, open_time: '09:00', close_time: '17:00' },
    FRIDAY: { enabled: false, open_time: '09:00', close_time: '17:00' },
    SATURDAY: { enabled: false, open_time: '09:00', close_time: '17:00' },
    SUNDAY: { enabled: false, open_time: '09:00', close_time: '17:00' },
  });

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getGBPDashboard(selectedClientId || undefined);
      setDashboard(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [selectedClientId]);

  const loadChartData = useCallback(async () => {
    if (!dashboard?.top_locations?.length) return;
    setChartLoading(true);
    try {
      const locationId = selectedLocation !== 'all'
        ? selectedLocation
        : dashboard.top_locations[0].id;
      const data = await api.getGBPLocationInsightsDaily(locationId, chartPeriod);
      setDailyInsights(data);
    } catch (err) {
      console.error('Failed to load chart data:', err);
    } finally {
      setChartLoading(false);
    }
  }, [dashboard?.top_locations, chartPeriod, selectedLocation]);

  useEffect(() => {
    loadDashboard();
    loadLocations();
  }, [loadDashboard]);

  useEffect(() => {
    if (dashboard) {
      loadChartData();
    }
  }, [dashboard, loadChartData]);

  useEffect(() => {
    if (activeTab === 'locations') {
      loadLocations();
    } else if (activeTab === 'reviews') {
      loadReviews();
    } else if (activeTab === 'posts') {
      loadPosts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedAccount, selectedLocation, searchTerm, ratingFilter, replyFilter, selectedClientId]);

  const loadLocations = async () => {
    try {
      const params: Record<string, string> = {};
      if (selectedClientId) params.client = selectedClientId;
      if (selectedAccount !== 'all') params.account = selectedAccount;
      if (searchTerm) params.search = searchTerm;

      const response = await api.getGBPLocations(params);
      setLocations(response.results);
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  };

  const loadReviews = async () => {
    try {
      const params: Record<string, string | boolean> = {};
      if (selectedClientId) params.client = selectedClientId;
      if (selectedLocation !== 'all') params.location = selectedLocation;
      if (ratingFilter !== 'all') params.star_rating = ratingFilter;
      if (replyFilter !== 'all') params.has_reply = replyFilter === 'replied';
      if (searchTerm) params.search = searchTerm;

      const response = await api.getGBPReviews(params);
      setReviews(response.results);
    } catch (err) {
      console.error('Failed to load reviews:', err);
    }
  };

  const loadPosts = async () => {
    try {
      const params: Record<string, string> = {};
      if (selectedClientId) params.client = selectedClientId;
      if (selectedLocation !== 'all') params.location = selectedLocation;
      if (searchTerm) params.search = searchTerm;

      const response = await api.getGBPPosts(params);
      setPosts(response.results);
    } catch (err) {
      console.error('Failed to load posts:', err);
    }
  };

  const handleSyncAll = async () => {
    if (!dashboard || dashboard.accounts_list.length === 0) return;
    
    // Sync each account
    for (const account of dashboard.accounts_list) {
      await handleSync(account.id);
    }
  };

  const handleSync = async (accountId: string) => {
    try {
      setSyncing(accountId);
      await api.syncGBPAccount(accountId);
      await loadDashboard();
      if (activeTab === 'locations') {
        await loadLocations();
      } else if (activeTab === 'reviews') {
        await loadReviews();
      }
    } catch (err: unknown) {
      console.error('Failed to sync account:', err);
      // Check if error is due to token expiry - auto-redirect to reconnect
      const errorMessage = err instanceof Error ? err.message : String(err);
      if (errorMessage.includes('token') || errorMessage.includes('401') || errorMessage.includes('refresh') || errorMessage.includes('unauthorized')) {
        // Token expired - redirect to OAuth reconnect
        handleConnectAccount();
        return;
      }
      setError('Failed to sync account. Please try reconnecting.');
    } finally {
      setSyncing(null);
    }
  };

  const handleConnectAccount = async () => {
    try {
      const { auth_url } = await api.getGBPAuthUrl();
      window.location.href = auth_url;
    } catch (err) {
      console.error('Failed to get auth URL:', err);
      setError('Failed to initiate Google Business Profile connection');
    }
  };

  const handleReplyToReview = async () => {
    if (!selectedReview || !replyText.trim()) return;

    try {
      setSubmittingReply(true);
      await api.replyToGBPReview(selectedReview.id, replyText);
      setShowReplyModal(false);
      setReplyText('');
      setSelectedReview(null);
      await loadReviews();
    } catch (err) {
      console.error('Failed to reply to review:', err);
    } finally {
      setSubmittingReply(false);
    }
  };

  const openReplyModal = (review: GBPReview) => {
    setSelectedReview(review);
    setReplyText(review.reply_comment || '');
    setShowReplyModal(true);
  };

  const openCreateLocationModal = () => {
    // Default to first account
    const firstAccount = dashboard?.accounts_list?.[0];
    if (firstAccount) {
      setCreateLocationAccountId(firstAccount.id);
    }
    setNewLocationData({ title: '', phone: '', website: '', primary_category_name: '', service_areas: '', description: '' });
    setNewLocationHours({
      MONDAY: { enabled: false, open_time: '09:00', close_time: '17:00' },
      TUESDAY: { enabled: false, open_time: '09:00', close_time: '17:00' },
      WEDNESDAY: { enabled: false, open_time: '09:00', close_time: '17:00' },
      THURSDAY: { enabled: false, open_time: '09:00', close_time: '17:00' },
      FRIDAY: { enabled: false, open_time: '09:00', close_time: '17:00' },
      SATURDAY: { enabled: false, open_time: '09:00', close_time: '17:00' },
      SUNDAY: { enabled: false, open_time: '09:00', close_time: '17:00' },
    });
    setCreateLocationError(null);
    setShowCreateLocationModal(true);
  };

  const handleCreateLocation = async () => {
    if (!newLocationData.title.trim() || !createLocationAccountId) return;

    try {
      setCreateLocationSubmitting(true);
      setCreateLocationError(null);

      const serviceAreas = newLocationData.service_areas
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

      const regularHours = Object.entries(newLocationHours)
        .filter(([, v]) => v.enabled)
        .map(([day, v]) => {
          const [openH, openM] = v.open_time.split(':').map(Number);
          const [closeH, closeM] = v.close_time.split(':').map(Number);
          return {
            day,
            open_time: openH + (openM || 0) / 60,
            close_time: closeH + (closeM || 0) / 60,
          };
        });

      await api.createGBPLocation(createLocationAccountId, {
        title: newLocationData.title,
        phone: newLocationData.phone || undefined,
        website: newLocationData.website || undefined,
        primary_category_name: newLocationData.primary_category_name || undefined,
        service_areas: serviceAreas.length > 0 ? serviceAreas : undefined,
        description: newLocationData.description || undefined,
        regular_hours: regularHours.length > 0 ? regularHours : undefined,
      });

      setShowCreateLocationModal(false);
      // Refresh locations list
      if (activeTab === 'locations') {
        await loadLocations();
      }
      await loadDashboard();
    } catch (err) {
      setCreateLocationError(err instanceof Error ? err.message : 'Failed to create location');
    } finally {
      setCreateLocationSubmitting(false);
    }
  };

  // Aggregate chart data by granularity, trimming incomplete periods
  const getChartData = () => {
    // Trim last 3 days (Google reporting delay)
    const trimmed = dailyInsights.slice(0, -3);

    if (chartGranularity === 'daily') {
      return trimmed.map(d => ({
        ...d,
        label: new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      }));
    }

    const groups: Record<string, GBPDailyInsight & { count: number; expectedDays: number }> = {};
    trimmed.forEach(d => {
      let key: string;
      const dt = new Date(d.date + 'T00:00:00');
      if (chartGranularity === 'weekly') {
        const dayOfWeek = dt.getDay();
        const monday = new Date(dt);
        monday.setDate(dt.getDate() - ((dayOfWeek + 6) % 7));
        key = monday.toISOString().slice(0, 10);
      } else {
        key = d.date.slice(0, 7);
      }
      if (!groups[key]) {
        groups[key] = { ...d, count: 1, expectedDays: chartGranularity === 'weekly' ? 7 : new Date(parseInt(key.slice(0, 4)), parseInt(key.slice(5, 7)), 0).getDate() };
      } else {
        groups[key].total_views += d.total_views;
        groups[key].search_views += d.search_views;
        groups[key].maps_views += d.maps_views;
        groups[key].website_clicks += d.website_clicks;
        groups[key].direction_requests += d.direction_requests;
        groups[key].phone_calls += d.phone_calls;
        groups[key].count += 1;
      }
    });

    const sorted = Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));

    // Drop the last group if incomplete (current week/month)
    const filtered = sorted.filter(([, val]) => val.count >= val.expectedDays);

    return filtered.map(([key, val]) => {
      let label: string;
      if (chartGranularity === 'weekly') {
        const dt = new Date(key + 'T00:00:00');
        label = `W ${dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      } else {
        const dt = new Date(key + '-01T00:00:00');
        label = dt.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      }
      return { ...val, label };
    });
  };

  const chartData = getChartData();

  const formatNumber = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return (num || 0).toLocaleString();
  };

  const getStarRatingClass = (rating: string) => {
    switch (rating) {
      case 'FIVE':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'FOUR':
        return 'bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300';
      case 'THREE':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'TWO':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'ONE':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const getPostTypeClass = (type: string) => {
    switch (type) {
      case 'STANDARD':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'EVENT':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'OFFER':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'ALERT':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 dark:text-gray-600'}`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-black">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-base font-bold text-gray-900 dark:text-white">Google Business Profile</h1>
              <p className="text-gray-600 dark:text-gray-300">
                Manage your business listings and reviews
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSyncAll}
                disabled={syncing !== null || !dashboard || dashboard.accounts_list.length === 0}
                className="flex items-center gap-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
                Sync
              </button>
              <button
                onClick={loadDashboard}
                disabled={loading}
                className="flex items-center gap-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={handleConnectAccount}
                className="flex items-center gap-2 bg-primary-500 text-black px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Connect Account
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 py-3">
        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-2 flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={loadDashboard}
              className="text-sm text-red-700 hover:text-red-900 underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Tabs and Location Filter */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
          <div className="flex gap-2">
            {(['overview', 'locations', 'reviews', 'posts'] as TabType[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === tab
                    ? 'bg-primary-500 text-black'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
          {locations.length > 1 && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="all">All Locations</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.title} — {loc.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && dashboard && (
          <div className="space-y-6">
            {/* Stats Cards */}
            {(() => {
              const loc = selectedLocation !== 'all'
                ? dashboard.top_locations.find(l => l.id === selectedLocation)
                : null;
              const stats = {
                totalLocations: loc ? 1 : dashboard.locations.total,
                avgRating: loc ? Number(loc.average_rating) : dashboard.locations.average_rating,
                totalReviews: loc ? loc.total_reviews : dashboard.locations.total_reviews,
                totalViews: loc ? loc.total_views : dashboard.locations.total_views,
                needsReply: loc ? loc.needs_reply_count : dashboard.reviews.needs_reply,
                postsTotal: loc ? loc.posts_count : (dashboard.posts?.total || 0),
                photosTotal: loc ? loc.photos_count : (dashboard.photos?.total || 0),
                websiteClicks: loc ? loc.website_clicks : dashboard.locations.total_website_clicks,
                directionRequests: loc ? loc.direction_requests : dashboard.locations.total_direction_requests,
                phoneCalls: loc ? loc.phone_calls : dashboard.locations.total_phone_calls,
              };
              return (<>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <MapPin className="w-5 h-5 text-blue-500" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  {formatNumber(stats.totalLocations)}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">{loc ? 'Location' : 'Total Locations'}</p>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  {stats.avgRating.toFixed(1)}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 flex items-center gap-1">Avg. Rating ({formatNumber(stats.totalReviews)} reviews) <Info className="w-3 h-3 text-gray-400" /></p>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <Eye className="w-5 h-5 text-purple-500" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  {formatNumber(stats.totalViews)}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">Total Views <span className="text-xs text-gray-400">(30d)</span></p>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <MessageSquare className="w-5 h-5 text-red-500" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  {formatNumber(stats.needsReply)}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">Reviews Need Reply</p>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <FileText className="w-5 h-5 text-indigo-500" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  {formatNumber(stats.postsTotal)}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">Posts</p>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <Camera className="w-5 h-5 text-pink-500" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  {formatNumber(stats.photosTotal)}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">Photos</p>
              </div>
            </div>

            {/* Actions Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <Globe className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatNumber(stats.websiteClicks)}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">Website Clicks <span className="text-xs text-gray-400">(30d)</span></p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Navigation className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatNumber(stats.directionRequests)}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">Directions <span className="text-xs text-gray-400">(30d)</span></p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <Phone className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatNumber(stats.phoneCalls)}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">Phone Calls <span className="text-xs text-gray-400">(30d)</span></p>
                  </div>
                </div>
              </div>
            </div>


            </>);
            })()}

            {/* API Note */}
            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
              <Info className="w-3 h-3" />
              <span>Note: Review counts from Google API may differ from public counts due to known API limitations.</span>
            </div>

            {/* Performance Charts */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-wrap gap-2">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Performance Trends</h2>
                <div className="flex items-center gap-2">
                  <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
                    {[
                      { days: 30, label: '30d' },
                      { days: 90, label: '90d' },
                      { days: 180, label: '6m' },
                      { days: 365, label: '12m' },
                    ].map(({ days, label }) => (
                      <button
                        key={days}
                        onClick={() => setChartPeriod(days)}
                        className={`px-2 py-1 text-xs rounded-md transition-colors ${
                          chartPeriod === days
                            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
                    {(['daily', 'weekly', 'monthly'] as const).map((g) => (
                      <button
                        key={g}
                        onClick={() => {
                          setChartGranularity(g);
                          if (g === 'monthly' && chartPeriod < 180) setChartPeriod(180);
                        }}
                        className={`px-2 py-1 text-xs rounded-md capitalize transition-colors ${
                          chartGranularity === g
                            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                        }`}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {chartLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : chartData.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <p>No performance data available. Run a sync to populate charts.</p>
                </div>
              ) : (
                <div className="p-4 space-y-6">
                  {/* Views Chart */}
                  <div>
                    <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">Views</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="#9ca3af" interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af" />
                        <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Area type="monotone" dataKey="search_views" name="Search" stroke="#6366f1" fill="#e0e7ff" stackId="views" />
                        <Area type="monotone" dataKey="maps_views" name="Maps" stroke="#06b6d4" fill="#cffafe" stackId="views" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Actions Chart */}
                  <div>
                    <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">Actions</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="#9ca3af" interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af" />
                        <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Area type="monotone" dataKey="website_clicks" name="Website Clicks" stroke="#22c55e" fill="#dcfce7" />
                        <Area type="monotone" dataKey="direction_requests" name="Directions" stroke="#3b82f6" fill="#dbeafe" />
                        <Area type="monotone" dataKey="phone_calls" name="Phone Calls" stroke="#a855f7" fill="#f3e8ff" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            {/* Recent Reviews */}
            {dashboard.recent_reviews.length > 0 && (
              <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Recent Reviews</h2>
                  <button
                    onClick={() => setActiveTab('reviews')}
                    className="text-sm text-primary-500 hover:text-primary-600"
                  >
                    View all
                  </button>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {dashboard.recent_reviews.map((review) => (
                    <div key={review.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-medium text-gray-900 dark:text-white">{review.reviewer_name}</span>
                            {renderStars(review.star_rating_int)}
                            <span className="text-sm text-gray-500 dark:text-gray-400">{formatDate(review.review_time)}</span>
                          </div>
                          <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-2">{review.comment || 'No comment'}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{review.location_title}</p>
                        </div>
                        {!review.has_reply && (
                          <button
                            onClick={() => openReplyModal(review)}
                            className="ml-4 px-3 py-1 text-sm bg-primary-500 text-black rounded-lg hover:bg-primary-600"
                          >
                            Reply
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Locations */}
            {dashboard.top_locations.length > 0 && (
              <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Top Locations</h2>
                  <button
                    onClick={() => setActiveTab('locations')}
                    className="text-sm text-primary-500 hover:text-primary-600"
                  >
                    View all
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Location</th>
                        <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Rating</th>
                        <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Reviews</th>
                        <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Views</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboard.top_locations.map((location) => (
                        <tr key={location.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="py-4 px-6">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{location.title}</p>
                              <p className="text-xs text-blue-600 dark:text-blue-400">{location.primary_category_display}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{location.full_address}</p>
                            </div>
                          </td>
                          <td className="py-4 px-6 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                              <span className="text-gray-900 dark:text-white">{Number(location.average_rating).toFixed(1)}</span>
                            </div>
                          </td>
                          <td className="py-4 px-6 text-right text-gray-900 dark:text-white">
                            {formatNumber(location.total_reviews)}
                          </td>
                          <td className="py-4 px-6 text-right text-gray-900 dark:text-white">
                            {formatNumber(location.total_views)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Locations Tab */}
        {activeTab === 'locations' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search locations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <select
                    value={selectedAccount}
                    onChange={(e) => setSelectedAccount(e.target.value)}
                    className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                  >
                    <option value="all">All Accounts</option>
                    {dashboard?.accounts_list.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.display_name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={openCreateLocationModal}
                    disabled={!dashboard || dashboard.accounts_list.length === 0}
                    className="flex items-center gap-2 bg-primary-500 text-black px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4" />
                    Add Location
                  </button>
                </div>
              </div>
            </div>

            {/* Locations Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {(() => {
                const filteredLocations = selectedLocation === 'all'
                  ? locations
                  : locations.filter(l => l.id === selectedLocation);
                return filteredLocations.length === 0 ? (
                  <div className="col-span-full bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                    <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">No locations found</p>
                  </div>
                ) : (
                  filteredLocations.map((location) => (
                  <div key={location.id} className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">{location.title}</h3>
                        <div className="flex flex-wrap gap-1 mt-1">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 text-xs rounded-full">
                            {location.primary_category_display}
                          </span>
                          {location.additional_categories?.slice(0, 3).map((cat, i) => (
                            <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 text-xs rounded-full">
                              {cat.display_name}
                            </span>
                          ))}
                        </div>
                      </div>
                      {location.is_verified && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 text-xs rounded-full">
                          Verified
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{location.full_address}</p>

                    <div className="flex items-center gap-4 mb-2">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <span className="font-medium text-gray-900 dark:text-white">{Number(location.average_rating).toFixed(1)}</span>
                      </div>
                      <span className="text-sm text-gray-500 dark:text-gray-400">({location.total_reviews} reviews)</span>
                    </div>

                    <div className="grid grid-cols-3 gap-4 text-center pt-4 border-t border-gray-100 dark:border-gray-800">
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{formatNumber(location.total_views)}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Views</p>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{formatNumber(location.website_clicks)}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Clicks</p>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{formatNumber(location.phone_calls)}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Calls</p>
                      </div>
                    </div>
                  </div>
                  ))
                );
              })()}
            </div>
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search reviews..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  <select
                    value={ratingFilter}
                    onChange={(e) => setRatingFilter(e.target.value)}
                    className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                  >
                    <option value="all">All Ratings</option>
                    <option value="FIVE">5 Stars</option>
                    <option value="FOUR">4 Stars</option>
                    <option value="THREE">3 Stars</option>
                    <option value="TWO">2 Stars</option>
                    <option value="ONE">1 Star</option>
                  </select>
                  <select
                    value={replyFilter}
                    onChange={(e) => setReplyFilter(e.target.value)}
                    className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                  >
                    <option value="all">All Reviews</option>
                    <option value="replied">Replied</option>
                    <option value="pending">Needs Reply</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Reviews List */}
            <div className="space-y-4">
              {!selectedClientId ? (
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                  <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">Select a client to view reviews</p>
                </div>
              ) : reviews.length === 0 ? (
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                  <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">No reviews found</p>
                </div>
              ) : (
                reviews.map((review) => (
                  <div key={review.id} className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        {review.reviewer_photo_url ? (
                          <img src={review.reviewer_photo_url} alt="" className="w-12 h-12 rounded-full" />
                        ) : (
                          <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                            <span className="text-gray-600 dark:text-gray-300 font-medium">
                              {review.reviewer_name.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-medium text-gray-900 dark:text-white">{review.reviewer_name}</span>
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStarRatingClass(review.star_rating)}`}>
                              {review.star_rating_int} Stars
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">{formatDate(review.review_time)}</span>
                          </div>
                          <p className="text-gray-600 dark:text-gray-300 mb-2">{review.comment || 'No comment'}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{review.location_title}</p>

                          {review.has_reply && review.reply_comment && (
                            <div className="mt-4 pl-4 border-l-2 border-primary-500">
                              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Your Reply:</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{review.reply_comment}</p>
                              <p className="text-xs text-gray-400 mt-1">{formatDate(review.reply_time)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => openReplyModal(review)}
                        className="ml-4 px-4 py-2 text-sm bg-primary-500 text-black rounded-lg hover:bg-primary-600 flex items-center gap-2"
                      >
                        <Send className="w-4 h-4" />
                        {review.has_reply ? 'Edit Reply' : 'Reply'}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Posts Tab */}
        {activeTab === 'posts' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search posts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Posts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {!selectedClientId ? (
                <div className="col-span-full bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">Select a client to view posts</p>
                </div>
              ) : posts.length === 0 ? (
                <div className="col-span-full bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">No posts found</p>
                </div>
              ) : (
                posts.map((post) => (
                  <div key={post.id} className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {post.media && post.media.length > 0 && post.media[0].google_url && (
                      <div className="h-48 bg-gray-100 dark:bg-gray-800">
                        <img
                          src={post.media[0].google_url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="p-3">
                      <div className="flex items-center justify-between mb-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getPostTypeClass(post.topic_type)}`}>
                          {post.topic_type}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">{formatDate(post.post_time)}</span>
                      </div>
                      <p className="text-gray-900 dark:text-white mb-3 line-clamp-3">{post.summary}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{post.location_title}</p>
                      {post.cta_type && post.cta_url && (
                        <a
                          href={post.cta_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 inline-flex items-center text-sm text-primary-500 hover:text-primary-600"
                        >
                          {post.cta_type.replace('_', ' ')} &rarr;
                        </a>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Reply Modal */}
      {showReplyModal && selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-lg">
            <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Reply to Review</h2>
              <button
                onClick={() => setShowReplyModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-3">
              {/* Original Review */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-2">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium text-gray-900 dark:text-white">{selectedReview.reviewer_name}</span>
                  {renderStars(selectedReview.star_rating_int)}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{selectedReview.comment || 'No comment'}</p>
              </div>

              {/* Reply Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Your Reply
                </label>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  placeholder="Write a professional response to this review..."
                />
              </div>
            </div>

            <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setShowReplyModal(false)}
                className="px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleReplyToReview}
                disabled={!replyText.trim() || submittingReply}
                className="px-4 py-2 bg-primary-500 text-black rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {submittingReply && <Loader2 className="w-4 h-4 animate-spin" />}
                {selectedReview.has_reply ? 'Update Reply' : 'Send Reply'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Location Modal */}
      {showCreateLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-900 z-10">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Create New Location</h2>
              <button
                onClick={() => setShowCreateLocationModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {createLocationError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded-lg text-sm">
                  {createLocationError}
                </div>
              )}

              {/* Account selector */}
              {dashboard && dashboard.accounts_list.length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account</label>
                  <select
                    value={createLocationAccountId}
                    onChange={(e) => setCreateLocationAccountId(e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2"
                  >
                    {dashboard.accounts_list.map((acc) => (
                      <option key={acc.id} value={acc.id}>{acc.display_name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Business Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Business Name *</label>
                <input
                  type="text"
                  value={newLocationData.title}
                  onChange={(e) => setNewLocationData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2"
                  placeholder="e.g. AMN Mobile Nails - Tampa"
                />
              </div>

              {/* Service Areas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Service Areas</label>
                <input
                  type="text"
                  value={newLocationData.service_areas}
                  onChange={(e) => setNewLocationData(prev => ({ ...prev, service_areas: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2"
                  placeholder="e.g. Tampa, FL, USA, St. Petersburg, FL, USA"
                />
                <p className="text-xs text-gray-500 mt-1">Comma-separated cities for service area businesses</p>
              </div>

              {/* Phone & Website */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                  <input
                    type="text"
                    value={newLocationData.phone}
                    onChange={(e) => setNewLocationData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Website</label>
                  <input
                    type="text"
                    value={newLocationData.website}
                    onChange={(e) => setNewLocationData(prev => ({ ...prev, website: e.target.value }))}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2"
                    placeholder="https://example.com"
                  />
                </div>
              </div>

              {/* Primary Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Primary Category</label>
                <input
                  type="text"
                  value={newLocationData.primary_category_name}
                  onChange={(e) => setNewLocationData(prev => ({ ...prev, primary_category_name: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2"
                  placeholder="e.g. gcid:nail_salon"
                />
                <p className="text-xs text-gray-500 mt-1">Google category ID (e.g. gcid:nail_salon, gcid:plumber)</p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea
                  value={newLocationData.description}
                  onChange={(e) => setNewLocationData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 resize-none"
                  placeholder="Describe your business..."
                />
              </div>

              {/* Business Hours */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Business Hours</label>
                <div className="space-y-2">
                  {Object.entries(newLocationHours).map(([day, hours]) => (
                    <div key={day} className="flex items-center gap-3">
                      <label className="flex items-center gap-2 w-28">
                        <input
                          type="checkbox"
                          checked={hours.enabled}
                          onChange={(e) => setNewLocationHours(prev => ({
                            ...prev,
                            [day]: { ...prev[day], enabled: e.target.checked }
                          }))}
                          className="rounded border-gray-300 dark:border-gray-600"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{day.slice(0, 3).toLowerCase()}</span>
                      </label>
                      {hours.enabled && (
                        <>
                          <input
                            type="time"
                            value={hours.open_time}
                            onChange={(e) => setNewLocationHours(prev => ({
                              ...prev,
                              [day]: { ...prev[day], open_time: e.target.value }
                            }))}
                            className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1 text-sm"
                          />
                          <span className="text-gray-500 text-sm">to</span>
                          <input
                            type="time"
                            value={hours.close_time}
                            onChange={(e) => setNewLocationHours(prev => ({
                              ...prev,
                              [day]: { ...prev[day], close_time: e.target.value }
                            }))}
                            className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1 text-sm"
                          />
                        </>
                      )}
                      {!hours.enabled && (
                        <span className="text-sm text-gray-400">Closed</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 sticky bottom-0 bg-white dark:bg-gray-900">
              <button
                onClick={() => setShowCreateLocationModal(false)}
                className="px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateLocation}
                disabled={!newLocationData.title.trim() || !createLocationAccountId || createLocationSubmitting}
                className="px-4 py-2 bg-primary-500 text-black rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {createLocationSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Create Location
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Account Card Component
const AccountCard = ({ account, onSync, syncing }: {
  account: GBPAccount;
  onSync: () => void;
  syncing: boolean;
}) => {
  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-4">
        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
          <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{account.display_name}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {account.account_type || 'Business Account'} | {account.client_name || 'No client linked'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="text-right">
          <p className="font-medium text-gray-900 dark:text-white">{account.locations_count}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Locations</p>
        </div>
        <button
          onClick={onSync}
          disabled={syncing}
          className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          title="Sync account"
        >
          <RefreshCw className={`w-5 h-5 text-gray-600 dark:text-gray-400 ${syncing ? 'animate-spin' : ''}`} />
        </button>
      </div>
    </div>
  );
};

export default GBP;
